import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import {
  ControlledTextField,
  GitHubIcon,
  GoogleIcon,
  MicrosoftIcon,
  LogoIcon,
  ProgressButton,
  SignUpInLegalFineprint,
} from '@localstack/ui';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Box, IconButton, InputAdornment, Link, Tooltip, Typography } from '@mui/material';
import {
  formatError,
  pushGTMEvent,
  useApiEffect,
  useRoutes,
  UserService,
  VALIDATION_RULES,
} from '@localstack/services';
import { useForm } from 'react-hook-form';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { ExternalLink, GTMSignupTriggers, PUBLIC_IDENTITY_PROVIDERS } from '@localstack/constants';
import { usePostHog } from 'posthog-js/react';

import { AppRoute } from '~/config';
import { useRecaptchaV3 } from '~/hooks/useRecaptchaV3';
import { useSSOStart } from '~/hooks/useSSOStart';

import { AuthLayout } from '../AuthLayout';
import {
  useStyles,
  OrDivider,
  AuthQueryParamKeys,
  PlanParamValues,
  useLegalDocument,
  usePostAuthRedirect,
} from '../common';

import { SignupFormData, STEPS_ENUM } from './types';
import {
  getEmulatorTypeFromQueryParams,
  getPlannedUsageTypeFromQueryParams,
  getPromoCodeFromQueryParam,
} from './utils';

const IDP_ICONS: Record<string, typeof GoogleIcon | null> = {
  google: GoogleIcon,
  microsoft: MicrosoftIcon,
  github: GitHubIcon,
};

export const SignUp = (): ReactElement => {
  const classes = useStyles();
  const posthog = usePostHog();
  const { search } = useLocation();
  const { initToken } = useRecaptchaV3();
  const { generateUrl } = useRoutes();
  const { redirect } = usePostAuthRedirect();
  const { isIdpLoading, initSSOFlow, selectedIdpName } = useSSOStart({
    onSuccess: (userInfo) => redirect(userInfo),
    onError: (err) => setSsoError(err.message),
  });

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const planQueryParam = queryParams.get(AuthQueryParamKeys.PLAN);
  const validatedPromoCode = getPromoCodeFromQueryParam(queryParams);
  const validatedEmulatorType = getEmulatorTypeFromQueryParams(queryParams);

  const isFreemiumPlanSignup = planQueryParam === PlanParamValues.FREE;
  const isAWSMarketplaceSignup = planQueryParam === PlanParamValues.MARKETPLACE;

  const legalDocument = useLegalDocument();

  const [step, setStep] = useState(STEPS_ENUM.LOGIN_DETAILS_STEP);
  const [ssoError, setSsoError] = useState<Optional<string>>(undefined);
  const [maskPassword, setMaskPassword] = useState<boolean>(false);
  const passwordFieldRef = useRef<HTMLInputElement>(null);

  const {
    control,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<SignupFormData>({
    mode: 'all',
  });

  const { signupUser, error: signupError } = useApiEffect(UserService, ['signupUser'], {
    suppressErrors: true,
  });
  const email = watch('email');

  const onSubmit = async (data: SignupFormData) => {
    const recaptchaToken = await initToken();
    const { email: e, password } = data;
    const signupSurveyData = {
      promo_code: validatedPromoCode,
      emulator: validatedEmulatorType,
      planned_usage_type: getPlannedUsageTypeFromQueryParams(queryParams),
    };
    const user = await signupUser({
      email: e,
      password,
      recaptcha_token: recaptchaToken,
      signup_survey: signupSurveyData,
    });
    if (user) {
      setStep(STEPS_ENUM.LS_CHECK_EMAIL_STEP);
      pushGTMEvent(GTMSignupTriggers.SIGNUP_SUCCESS, posthog);
    }
  };

  const getPageTitle = useCallback(() => {
    if (step === STEPS_ENUM.LS_CHECK_EMAIL_STEP) {
      return 'Check your email';
    }

    // NOTE
    // Captialization and exact wording are of legal significance
    // Any changes to this need to be reviewed by legal
    if (isFreemiumPlanSignup) return 'Sign Up for the Hobby Plan';
    if (isAWSMarketplaceSignup) return 'Sign Up for LocalStack';

    return 'Sign up for a free LocalStack trial';
  }, [step, isFreemiumPlanSignup]);

  return (
    <AuthLayout wideLayout={step === STEPS_ENUM.LS_CHECK_EMAIL_STEP} documentTitle="Sign Up">
      <Box textAlign="center">
        <Box mb={4}>
          <LogoIcon />
        </Box>
        <Typography variant="h3" component="h2" fontWeight={600}>
          {getPageTitle()}
        </Typography>
        {step !== STEPS_ENUM.LS_CHECK_EMAIL_STEP && (
          <Box mt={1}>
            <Typography fontSize={13} color="text.tertiary" fontWeight={500}>
              Already have an account?{' '}
              <Link
                component={RouterLink}
                underline="hover"
                color="text.primary"
                display="inline-flex"
                alignItems="center"
                columnGap={0.5}
                tabIndex={0}
                to={generateUrl(AppRoute.SIGN_IN, {}, undefined, { keepQueryParams: true })}
              >
                Log in
              </Link>
            </Typography>
            {isFreemiumPlanSignup && (
              <Typography fontSize={13} color="text.tertiary" fontWeight={500}>
                Are you a student?{' '}
                <Link
                  href={ExternalLink.LOCALSTACK_FOR_STUDENTS}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  color="text.primary"
                >
                  Start here
                </Link>
              </Typography>
            )}
          </Box>
        )}

        <>
          {signupError && step === STEPS_ENUM.LOGIN_DETAILS_STEP && (
            <Box mt={2}>
              <Alert sx={{ textAlign: 'left' }} severity="error" variant="outlined">
                {formatError(signupError)}
              </Alert>
            </Box>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            {step === STEPS_ENUM.LOGIN_DETAILS_STEP && (
              <>
                <Box display="flex" flexDirection="column" rowGap={1} mt={3.5}>
                  {PUBLIC_IDENTITY_PROVIDERS.map(({ orgId, idpName, label }) => {
                    const Icon = IDP_ICONS[idpName];
                    return (
                      <ProgressButton
                        key={idpName}
                        loading={isIdpLoading && selectedIdpName === idpName}
                        variant="outlined"
                        color="tertiary"
                        fullWidth
                        size="xlarge"
                        data-sso-button={`${orgId}/${idpName}`}
                        sx={{ textTransform: 'none' }}
                        startIcon={Icon && <Icon sx={{ fontSize: 16 }} />}
                        onClick={() => {
                          setSsoError(undefined);
                          initSSOFlow({
                            orgId: orgId,
                            idpName: idpName,
                          });
                        }}
                      >
                        Continue with {label}
                      </ProgressButton>
                    );
                  })}
                </Box>
                {ssoError && (
                  <Box mt={1}>
                    <Typography fontSize={13} fontWeight={500} color="error">
                      {ssoError}
                    </Typography>
                  </Box>
                )}
                <Box my={2}>
                  <OrDivider />
                </Box>
                <Box display="flex" flexDirection="column" rowGap={1}>
                  <ControlledTextField
                    control={control}
                    showErrorsOnlyIfTouched={false}
                    name="email"
                    fullWidth
                    placeholder="Enter your email address"
                    type="text"
                    variant="outlined"
                    rules={{
                      ...VALIDATION_RULES.email,
                      ...VALIDATION_RULES.required,
                    }}
                  />
                  {/* hack to avoid the prefill issues with react-hook-form */}
                  <Box>
                    <ControlledTextField
                      control={control}
                      showErrorsOnlyIfTouched={false}
                      name="password"
                      ref={passwordFieldRef}
                      fullWidth
                      placeholder="Create your password"
                      type={maskPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={`${maskPassword ? 'Hide' : 'Show'} Password`}>
                              <IconButton onClick={() => setMaskPassword((show) => !show)} size="small">
                                {maskPassword ? (
                                  <VisibilityOffIcon fontSize="small" />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      rules={{ ...VALIDATION_RULES.password }}
                    />
                  </Box>
                  <ProgressButton
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="xlarge"
                    type="submit"
                    loading={isSubmitting}
                  >
                    Sign Up
                  </ProgressButton>
                </Box>
              </>
            )}
          </form>
          {step === STEPS_ENUM.LOGIN_DETAILS_STEP && (
            <>
              <Box mt={2} px={2.5}>
                <SignUpInLegalFineprint legalDocument={legalDocument} />
              </Box>
            </>
          )}
          {step === STEPS_ENUM.LS_CHECK_EMAIL_STEP && (
            <Box mt={4}>
              <Typography fontSize={13} className={classes.ellipsisBox} color="text.tertiary" fontWeight={500}>
                We&apos;ve sent an account activation link.
                <br />
                Please check your inbox at{' '}
                <Typography component="span" color="text.primary">
                  {email}
                </Typography>
                .
              </Typography>
              <Typography fontSize={13} fontWeight={500} mt={4}>
                <Link
                  component={RouterLink}
                  underline="hover"
                  display="inline-flex"
                  alignItems="center"
                  columnGap={0.5}
                  tabIndex={0}
                  to={AppRoute.SIGN_IN}
                  color="text.primary"
                >
                  Back to login
                </Link>
              </Typography>
            </Box>
          )}
        </>
      </Box>
    </AuthLayout>
  );
};
